import {useCallback, useEffect, useRef, useState} from "react";
import useNotification from "../utils/notificationUtil";
import useConfirmDialog from "../components/ConfirmDialog";

export interface UseMutationHelperProps {
  mutationFn: () => Promise<boolean | string>,
  onClear?: () => void;
  validate?: () => boolean | string,
  onCanceled?: () => void,
  onCompleted?: () => void,
  onError?: (err: unknown) => void,
  successMsg?: string
  errMsg?: string
  validationErrMsg?: string
  enableConfirm?: boolean
}

export default function useMutation(params: UseMutationHelperProps) {
  const notification = useNotification();
  const [isClear, setIsClear] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {ConfirmDialog, openConfirmDialog} = useConfirmDialog();
  const formRef = useRef<HTMLFormElement>(null);

  const clear = () => {
    setIsLoading(true);
    params?.onClear?.();
    setIsClear(true); // 各入力コントロールのerrを初期化
    notification.infoMsg("クリアしました。");
    setIsLoading(false);
  }

  // isClearの戻し処理
  useEffect(() => {
    if (isClear) setIsClear(false);
  }, [isClear]);

  const handleError = useCallback(({error, message}: { error?: unknown, message?: string } = {}) => {
    console.error(`${message ?? "エラーが発生しました。"}: `, error);
    notification.errorMsg(message ?? "エラーが発生しました。");
    return false;
  }, [notification]);

  const mutate = async () => {
    try {
      // --------------------------------------------------
      // Validation Step
      // --------------------------------------------------
      if (formRef.current && !formRef.current.reportValidity()) {
        notification.errorMsg("入力内容に誤りがあります。");
        return;
      }

      if (params.validate) {
        const msg = params.validate();
        if (msg !== true) {
          notification.errorMsg(typeof msg === "string"
            ? msg
            : params.validationErrMsg ?? "入力内容に誤りがあります。"
          );
          return;
        }
      }

      // --------------------------------------------------
      // Confirm Step
      // --------------------------------------------------
      if (params.enableConfirm ?? true) {
        const result = await openConfirmDialog();
        if (result !== "confirm") {
          params.onCanceled?.();
          return;
        }
      }

      // --------------------------------------------------
      // Callback
      // --------------------------------------------------
      setIsLoading(true);

      const result = await params.mutationFn();
      if (result === true) {
        if (params.onCompleted) {
          params.onCompleted();
        } else {
          notification.infoMsg(params.successMsg ?? "処理が完了しました。");
        }
      } else {
        notification.errorMsg(typeof result === "string"
          ? result
          : params.errMsg ?? "エラーが発生しました。"
        );
      }
    } catch (error) {
      if (params.onError) {
        params.onError(error);
      } else {
        handleError({error, message: params.errMsg});
      }
    } finally {
      setIsLoading(false);
    }
  }

  return {
    isClear,
    clear,
    isLoading,
    ConfirmDialog,
    formRef,
    mutate,
  }
}
